import { useState, type HTMLAttributes, type ReactNode } from 'react';
import { Icon, IconSlot, type IconName } from './icons.js';
import { Avatar, type AvatarColor } from './primitives.js';

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

const SIDEBAR_DARK_KEY = 'hp-sidebar-dark';

function readStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // storage may be unavailable (private mode); the preference is a convenience only
  }
}

/** Dark-sidebar preference, remembered per browser exactly like the prototype (`hp-sidebar-dark`). */
export function useSidebarTheme(): { dark: boolean; toggle: () => void } {
  const [dark, setDark] = useState(() => readStorage(SIDEBAR_DARK_KEY) === '1');
  return {
    dark,
    toggle: () => {
      setDark((d) => {
        writeStorage(SIDEBAR_DARK_KEY, d ? '0' : '1');
        return !d;
      });
    },
  };
}

// --- brand ---

export interface SystemBrandProps {
  href: string;
  /** Home Portal wordmark, light background variant. */
  logoSrc: string;
  /** Wordmark for the dark sidebar. */
  logoDarkSrc: string;
  ariaLabel: string;
  onClick?: () => void;
}

/** `.brand.hp-system-brand` — the corporate mark (the only non-Lucide SVG) with the wordmark and "BackOffice". */
export function SystemBrand({ href, logoSrc, logoDarkSrc, ariaLabel, onClick }: SystemBrandProps) {
  return (
    <a className="brand hp-system-brand" href={href} aria-label={ariaLabel} onClick={onClick}>
      <svg
        className="hp-system-brand__mark"
        viewBox="0 0 34.04 39.423"
        aria-hidden="true"
        focusable="false"
        fill="currentColor"
      >
        <path d="M23.854,28.863,30.5,35.507V14.989L23.854,18Z" transform="translate(3.535 2.221)" />
        <path d="M0,35.515l6.64-6.641V18.005L0,14.969Z" transform="translate(0 2.218)" />
        <path d="M15.7,0,.785,14.972l6.64,3.035L15.7,9.7Z" transform="translate(.116 0)" />
        <path d="M15.86,0V9.7l8.282,8.311L30.8,14.987Z" transform="translate(2.35 0)" />
        <path d="M8.12,28.547,1.472,35.193H32.127l-6.643-6.646Z" transform="translate(.218 4.23)" />
      </svg>
      <div className="hp-system-brand__copy">
        <img
          className="hp-system-brand__logo hp-system-brand__logo--light"
          src={logoSrc}
          width={128}
          height={23}
          alt="Home Portal"
        />
        <img
          className="hp-system-brand__logo hp-system-brand__logo--dark"
          src={logoDarkSrc}
          width={128}
          height={23}
          alt="Home Portal"
        />
        <strong>BackOffice</strong>
      </div>
    </a>
  );
}

// --- sidebar ---

export interface SidebarProps {
  dark?: boolean;
  /** Off-canvas state under 800 px. */
  mobileOpen?: boolean;
  brand: ReactNode;
  children: ReactNode;
  /** `.sidebar-bottom`: theme toggle, guide, user. */
  bottom?: ReactNode;
  ariaLabel?: string;
}

/** `aside.sidebar(.dark)(.mobile-open)` — 228 px fixed panel: brand, `.sidebar-navigation`, `.sidebar-bottom`. */
export function Sidebar({ dark, mobileOpen, brand, children, bottom, ariaLabel = 'Main navigation' }: SidebarProps) {
  return (
    <aside className={cx('sidebar', dark && 'dark', mobileOpen && 'mobile-open')} aria-label={ariaLabel}>
      {brand}
      <div className="sidebar-navigation">{children}</div>
      {bottom && <div className="sidebar-bottom">{bottom}</div>}
    </aside>
  );
}

export interface NavSectionProps {
  /** Storage key suffix; the open state is remembered as `hp-menu-<id>`. */
  id: string;
  label: string;
  /** Forced open (the section holding the current page). */
  forceOpen?: boolean;
  defaultOpen?: boolean;
  /** Locked preview section: lock icon, no summary background. */
  locked?: boolean;
  children: ReactNode;
  ariaLabel?: string;
}

/** `details.nav-section` — uppercase summary with a chevron, links in `nav.section-links`. */
export function NavSection({ id, label, forceOpen, defaultOpen = true, locked, children, ariaLabel }: NavSectionProps) {
  const key = `hp-menu-${id}`;
  const [open, setOpen] = useState(() => {
    const stored = readStorage(key);
    return stored === null ? defaultOpen : stored === '1';
  });
  // The section holding the current page is always open (the prototype forces it too).
  const isOpen = Boolean(forceOpen) || open;
  return (
    <details
      className={cx('nav-section', locked && 'preview-locked')}
      id={`${id}-menu`}
      open={isOpen}
      onToggle={(e) => {
        const next = e.currentTarget.open;
        if (next === isOpen) return;
        setOpen(next);
        writeStorage(key, next ? '1' : '0');
      }}
    >
      <summary>
        <span>{label}</span>
        {locked ? <IconSlot name="lock" className="section-lock" /> : <IconSlot name="chevronDown" />}
      </summary>
      <nav className="section-links" aria-label={ariaLabel ?? label}>
        {children}
      </nav>
    </details>
  );
}

export interface NavItemState {
  /** The current page. */
  active?: boolean;
  /** A parent of the current page (detail open under this section). */
  currentParent?: boolean;
}

/** Class list of `.nav-item` for whatever link element the app renders. */
export function navItemClass({ active, currentParent }: NavItemState): string {
  return cx('nav-item', (active || currentParent) && 'active', currentParent && 'is-current-parent');
}

/** `aria-current` value matching the state (page / location). */
export function navItemCurrent({ active, currentParent }: NavItemState): 'page' | 'location' | undefined {
  if (active) return 'page';
  if (currentParent) return 'location';
  return undefined;
}

/** Icon + label content of a `.nav-item`, optionally with a trailing `.nav-count`. */
export function NavItemContent({ icon, children, count }: { icon: IconName; children: ReactNode; count?: ReactNode }) {
  return (
    <>
      <Icon name={icon} />
      <span>{children}</span>
      {count !== undefined && <span className="nav-count">{count}</span>}
    </>
  );
}

/** `.sidebar-theme` — light/dark sidebar switch with `aria-pressed`. */
export function SidebarThemeToggle({
  dark,
  onToggle,
  labelDark = 'Dark panel',
  labelLight = 'Dark panel',
  ariaLabel,
}: {
  dark: boolean;
  onToggle: () => void;
  labelDark?: string;
  labelLight?: string;
  /** Overrides the English default ("Switch the panel to light/dark"). */
  ariaLabel?: string;
}) {
  return (
    <button
      type="button"
      className="sidebar-theme"
      aria-pressed={dark}
      aria-label={ariaLabel ?? (dark ? 'Switch the panel to light' : 'Switch the panel to dark')}
      onClick={onToggle}
    >
      <IconSlot name="circle" />
      <span>{dark ? labelDark : labelLight}</span>
      <span className="theme-switch" aria-hidden="true" />
    </button>
  );
}

/** `.sidebar-theme`-styled row that switches the UI language to the one `label` names. */
export function SidebarLanguageToggle({
  label,
  ariaLabel,
  onClick,
}: {
  label: string;
  ariaLabel?: string;
  onClick: () => void;
}) {
  return (
    <button type="button" className="sidebar-theme" aria-label={ariaLabel} onClick={onClick}>
      <IconSlot name="globe" />
      <span>{label}</span>
    </button>
  );
}

/** `.sidebar-help` — guide link with an optional version tag. */
export function SidebarHelp({
  href,
  children,
  version,
  onClick,
}: {
  href: string;
  children: ReactNode;
  version?: string;
  onClick?: () => void;
}) {
  return (
    <a className="sidebar-help" href={href} onClick={onClick}>
      <IconSlot name="book" />
      <span>{children}</span>
      {version && <span className="version">{version}</span>}
    </a>
  );
}

export interface UserProfileProps {
  name: string;
  role: ReactNode;
  color?: AvatarColor;
  onClick?: () => void;
  ariaLabel?: string;
  /** Icon on the right (chevron by default). */
  trailingIcon?: IconName;
}

/** `.user-profile` — avatar, name, role, chevron; the sidebar's account entry. */
export function UserProfile({
  name,
  role,
  color = 'navy',
  onClick,
  ariaLabel,
  trailingIcon = 'chevronDown',
}: UserProfileProps) {
  return (
    <button type="button" className="user-profile" onClick={onClick} aria-label={ariaLabel}>
      <Avatar name={name} color={color} />
      <span>
        <strong>{name}</strong>
        <small>{role}</small>
      </span>
      <Icon name={trailingIcon} />
    </button>
  );
}

// --- shell frame ---

/** `a.skip-link` + `aside` + `div.app-shell > header.topbar + main#main`. */
export function AppShell({
  sidebar,
  topbar,
  children,
  skipLabel = 'Skip to content',
}: {
  sidebar: ReactNode;
  topbar: ReactNode;
  children: ReactNode;
  skipLabel?: string;
}) {
  return (
    <>
      <a className="skip-link" href="#main">
        {skipLabel}
      </a>
      {sidebar}
      <div className="app-shell">
        {topbar}
        <main id="main" tabIndex={-1}>
          {children}
        </main>
      </div>
    </>
  );
}

export interface TopbarProps {
  /** Breadcrumb nodes: the app renders links; separators are inserted here. */
  breadcrumbs: ReactNode[];
  actions?: ReactNode;
  onMenu?: () => void;
  menuLabel?: string;
  className?: string;
}

/** `header.topbar` — mobile menu button, `.breadcrumbs`, `.topbar-actions`. */
export function Topbar({ breadcrumbs, actions, onMenu, menuLabel = 'Open navigation', className }: TopbarProps) {
  return (
    <header className={cx('topbar', className)}>
      <button type="button" className="icon-button mobile-menu" aria-label={menuLabel} onClick={onMenu}>
        <IconSlot name="menu" />
      </button>
      <div className="breadcrumbs">
        {breadcrumbs.map((node, i) => (
          <span key={i} className="bo-crumb">
            {i > 0 && <Icon name="chevronRight" />}
            {node}
          </span>
        ))}
      </div>
      {actions && <div className="topbar-actions">{actions}</div>}
    </header>
  );
}

/** `#toast.toast(.visible)` — the single status message, bottom centre. */
export function Toast({
  message,
  visible,
  ...rest
}: HTMLAttributes<HTMLDivElement> & { message: ReactNode; visible: boolean }) {
  return (
    <div id="toast" className={cx('toast', visible && 'visible')} role="status" aria-live="polite" {...rest}>
      {message}
    </div>
  );
}
